import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "uxp-for-extendscript-developers"
    }}>{`UXP for ExtendScript Developers`}</h1>
    <p>{`If you're coming to UXP from ExtendScript and the ESTK (ExtendScript ToolKit) or its 64-bit successor, the `}<a parentName="p" {...{
        "href": "https://marketplace.visualstudio.com/items?itemName=Adobe.extendscript-debug"
      }}>{`ExtendScript Debugger`}</a>{`, you'll be pleasantly surprised but you'll also have some new things to learn:`}</p>
    <h3 {...{
      "id": "different-dom-access"
    }}>{`Different DOM access`}</h3>
    <p>{`UXP provides different methods for accessing the Photoshop DOM. See the `}<a parentName="p" {...{
        "href": "/photoshop/uxp/../../ps_reference/"
      }}>{`Photoshop UXP API reference`}</a>{` for more information. At present, the entire DOM is not yet supported, but more access is being added with every Photoshop release.`}</p>
    <p>{`As a workaround until all the significant Photoshop features are accessible via UXP, you can use a feature called `}<a parentName="p" {...{
        "href": "../../ps_reference/media/batchplay/"
      }}>{`batchPlay`}</a>{`.`}</p>
    <h3 {...{
      "id": "migration-helper-for-extendscript-developers"
    }}>{`Migration helper for ExtendScript developers`}</h3>
    <p>{`If you use `}<inlineCode parentName="p">{`executeAction`}</inlineCode>{` and `}<inlineCode parentName="p">{`executeActionGet`}</inlineCode>{` often in your code, the `}<a parentName="p" {...{
        "href": "https://github.com/adobe-uxp/ps-es-to-uxp"
      }}>{`ExtendScript batchPlay logger`}</a>{` utility can help you migrate your code. Plug the `}<inlineCode parentName="p">{`ps-es-to-uxp`}</inlineCode>{` jsx code into your ExtendScript project, and it will print out all your `}<inlineCode parentName="p">{`executeAction`}</inlineCode>{` and `}<inlineCode parentName="p">{`executeActionGet`}</inlineCode>{` methods in a format suitable for the UXP equivalent API: `}<a parentName="p" {...{
        "href": "../../ps_reference/media/batchplay"
      }}>{`batchPlay`}</a>{`.`}</p>
    <h3 {...{
      "id": "development-environment"
    }}>{`Development environment`}</h3>
    <p>{`ExtendScript Toolkit ("ESTK") was the development environment of choice for many years, and still is for many Windows developers. On macOS, ESTK died a lingering death due to the deprecation of 32-bit code. Recently, ESTK has been replaced by a plugin for Visual Studio Code, a widely-used editor.`}</p>
    <p>{`UXP source code (which is just HTML, CSS, and JavaScript) can be developed in the programming editor of your choice, although many in the UXP world prefer VS Code due to its powerful extensibility features.`}</p>
    <h3 {...{
      "id": "user-interface"
    }}>{`User interface`}</h3>
    <p>{`In some ExtendScripts, there is generally little to no UI; the end user picks a script from the File->Scripts menu, and the script runs facelessly. If your ExtendScript  `}<em parentName="p">{`does`}</em>{` need a UI, you typically use simple JavaScript `}<inlineCode parentName="p">{`alert()`}</inlineCode>{`, `}<inlineCode parentName="p">{`confirm()`}</inlineCode>{`, and `}<inlineCode parentName="p">{`prompt()`}</inlineCode>{` calls. Or you could use the full-featured but complex `}<a parentName="p" {...{
        "href": "https://creativepro.com/files/kahrel/indesign/scriptui.html"
      }}>{`ScriptUI`}</a>{` to create complex dialogs. Moving beyond ExtendScript into CEP, there are more possibilities with panels.`}</p>
    <p>{`In UXP, on the other hand, you're able to design as simple or as complex a UI as you want, merely by using HTML and CSS to create the visual part, and JavaScript to create the UI's underlying logic (e.g., "what should I do when this button is clicked").`}</p>
    <h3 {...{
      "id": "modern-javascript"
    }}>{`Modern JavaScript`}</h3>
    <p>{`ExtendScript uses a very old version of JavaScript (ES3), from the era when JavaScript was coded on punched cards. In contrast, UXP uses the V8 JavaScript engine which supports ES6; this has a number of important features lacking in ExtendScript. Not all of the newer features are used and/or supported in the UXP world, but as an ExtendScript developer you'll see enough puzzling syntax in UXP examples that you should probably familiarize yourself with ECMAScript ES6 (the standards body name for JavaScript) so you can understand the sample code.`}</p>
    <p>{`A full introduction to ES6 features and syntax is beyond the scope of this documentation. You can find many websites that discuss ES6 features, including the popular `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript"
      }}>{`Mozilla Developer Network`}</a>{`. Make sure you understand at least these language features, which you will find in UXP code you pick up from around the web:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements"
        }}>{`"const" and "let" declarations vs "var"`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Learn/JavaScript/Asynchronous"
        }}>{`Promises and asynchronous functions`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions"
        }}>{`Anonymous functions`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/Arrow_functions"
        }}>{`Arrow functions`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals"
        }}>{`Template literals`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map"
        }}>{`Maps`}</a></li>
    </ul>
    <h3 {...{
      "id": "whats-missing-in-uxp"
    }}>{`What's Missing in UXP`}</h3>
    <p>{`UXP provides an HTML interpreter similar to Chromium; it's more limited than a full browser, which means some commonly-used web CSS and HTML idioms don't work. For more information on what does and doesn't work in the UXP HTML environment, see `}<a parentName="p" {...{
        "href": "/photoshop/uxp/guides/uxp_guide/unsupported/"
      }}>{`this document`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      